import React, { useEffect, useState } from "react";
import {
  Button,
  Select,
  Input,
  Form,
  message,
  Table,
  TableColumnsType,
} from "antd";
import {
  ConversationInquiryTypeEnum,
  ConversationInquiryTypeText,
} from "common/enums/ConversationInquiryType.enum";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import {
  cleanDataConversationInquiry,
  createConversationInquiry,
} from "reducers/ConversationInquirySlice";
import { Link, useNavigate } from "react-router-dom";
import { Inquiry } from "common/interfaces";
import { usePagination } from "common/hooks/usePagination";
import InquiryItem from "components/InquiryItem";
import PaginateListMobile from "components/PaginateListMobile";
import { useGetInquiriesQuery } from "reducers/inquiryApi";
import {
  INQUIRY_FILTER_SORT,
  INQUIRY_FILTER_STATUS_TYPE,
} from "common/constants";
import { UTCToJapanTime } from "common/helper/time.helper";
import { parseErrorMessage } from "common/utils";
import { IconKeyboardArrowRightBlue } from "assets/svg";
import { ConversationInquiryStatusText } from "common/enums/ConversationInquiryStatus.enum";
import { isNew } from "common/helper/status.helper";
import { useCommonContext } from "contexts/CommonContext";

const columns: TableColumnsType<Inquiry> = [
  {
    title: "お問い合わせID",
    dataIndex: "id",
    align: "center",
    render: (value: number, record: Inquiry) => (
      <>
        {isNew(record.created_at) && (
          <span className="bg-primary text-white px-2 py-1 rounded-[6px] mr-1">
            New
          </span>
        )}
        <Link
          to={`/seller/conversation-inquiry/${record.id}`}
          className="text-primary"
        >
          #{record.code}
        </Link>
      </>
    ),
  },
  {
    title: "件名",
    dataIndex: "subject",
    align: "center",
  },
  {
    title: "種別",
    dataIndex: "type",
    align: "center",
    render: (value) => ConversationInquiryTypeText(value),
  },
  {
    title: "ステータス",
    dataIndex: "status",
    align: "center",
    render: (value) => ConversationInquiryStatusText(value),
  },
  {
    title: "最終更新",
    dataIndex: "created_at",
    align: "center",
    render: (value) => {
      const timeJapan = UTCToJapanTime(value);
      return `${timeJapan.year}/${timeJapan.month}/${timeJapan.day} ${timeJapan.hour}:${timeJapan.minute}`;
    },
  },
  {
    title: "操作",
    dataIndex: "id",
    align: "center",
    render: (value: number, record: Inquiry) => (
      <>
        <Link
          to={`/seller/conversation-inquiry/${record.id}`}
          className="text-primary"
        >
          詳細画面へ
        </Link>
      </>
    ),
  },
];

const ConversationInquiryPage = () => {
  const { setHeaderTitle } = useCommonContext();
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const [errors] = useState<{ [key: string]: boolean }>({});
  const { conversationInquiryData } = useSelector(
    (state: RootState) => state.conversationInquiry,
  );
  const { userData } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();
  const inquiryTypeOptions = [
    { value: ConversationInquiryTypeEnum.ABOUT_LISTING, label: "出品について" },
    {
      value: ConversationInquiryTypeEnum.ABOUT_TRANSACTION,
      label: "取引について",
    },
    { value: ConversationInquiryTypeEnum.ABOUT_ISSUE, label: "不具合について" },
    { value: ConversationInquiryTypeEnum.OTHERS, label: "その他" },
  ];

  // Handle form submission
  const handleSubmit = async (values: any) => {
    dispatch(
      createConversationInquiry({
        ...values,
        company_id: userData.id,
      }),
    );
    message.success("お問い合わせを送信しました");
  };

  const {
    pagination: paginationProcess,
    paginationMobile: paginationMobileProcess,
    onChangePage: onChangePageProcess,
    updateTotal: updateTotalProcess,
    updateTotalMobile: updateTotalMobileProcess,
  } = usePagination();

  const {
    pagination: paginationClose,
    paginationMobile: paginationMobileClose,
    onChangePage: onChangePageClose,
    updateTotal: updateTotalClose,
    updateTotalMobile: updateTotalMobileClose,
  } = usePagination();

  const inquiryList = {
    search: "",
    type: [],
    sort: INQUIRY_FILTER_SORT.NEWEST?.value,
  };

  const inquiryListProcess = {
    ...inquiryList,
    status: [
      INQUIRY_FILTER_STATUS_TYPE["UNRESOLVED"].value,
      INQUIRY_FILTER_STATUS_TYPE["PROCESSING"].value,
    ],
  };

  const inquiryListClose = {
    ...inquiryList,
    status: [INQUIRY_FILTER_STATUS_TYPE["INQUIRY_CLOSED"].value],
  };

  const { data: inquiryDataProcess, error: inquiryErrProcess } =
    useGetInquiriesQuery({
      filter: inquiryListProcess,
      page: paginationProcess.current,
      limit: 3,
    });

  const { data: inquiryMobileDataProcess, error: inquiryMobileErrProcess } =
    useGetInquiriesQuery({
      filter: inquiryListProcess,
      page: paginationMobileProcess.current,
      limit: 3,
    });

  const { data: inquiryDataClose, error: inquiryErrClose } =
    useGetInquiriesQuery({
      filter: inquiryListClose,
      page: paginationClose.current,
      limit: 5,
    });

  const { data: inquiryMobileDataClose, error: inquiryMobileErrClose } =
    useGetInquiriesQuery({
      filter: inquiryListClose,
      page: paginationMobileClose.current,
      limit: 5,
    });

  useEffect(() => {
    if (inquiryDataProcess?.data?.meta?.total_items) {
      updateTotalProcess(inquiryDataProcess.data.meta.total_items);
    }
  }, [inquiryDataProcess]);

  useEffect(() => {
    if (inquiryMobileDataProcess?.data?.meta?.total_items) {
      updateTotalMobileProcess(inquiryMobileDataProcess.data.meta.total_items);
    }
  }, [inquiryMobileDataProcess]);

  useEffect(() => {
    if (inquiryDataClose?.data?.meta?.total_items) {
      updateTotalClose(inquiryDataClose.data.meta.total_items);
    }
  }, [inquiryDataClose]);

  useEffect(() => {
    if (inquiryMobileDataClose?.data?.meta?.total_items) {
      updateTotalMobileClose(inquiryMobileDataClose.data.meta.total_items);
    }
  }, [inquiryMobileDataClose]);

  useEffect(() => {
    if (inquiryErrProcess)
      void message.error(parseErrorMessage(inquiryErrProcess));
  }, [inquiryErrProcess]);

  useEffect(() => {
    if (inquiryMobileErrProcess)
      void message.error(parseErrorMessage(inquiryMobileErrProcess));
  }, [inquiryMobileErrProcess]);

  useEffect(() => {
    if (inquiryErrClose) void message.error(parseErrorMessage(inquiryErrClose));
  }, [inquiryErrClose]);

  useEffect(() => {
    if (inquiryMobileErrClose)
      void message.error(parseErrorMessage(inquiryMobileErrClose));
  }, [inquiryMobileErrClose]);

  useEffect(() => {
    if (conversationInquiryData) {
      navigate(
        "/seller/conversation-inquiry/" + conversationInquiryData?.data.id,
      );
    }
  });

  useEffect(() => {
    setHeaderTitle("お問い合わせ");
    return () => {
      dispatch(cleanDataConversationInquiry());
    };
  });

  return (
    <div className="self-stretch min-h-[924px] p-[20px] pb-[100px] md:p-10 bg-white inline-flex flex-col justify-start items-start gap-[30px] md:gap-12 overflow-hidden conversation-inquiry">
      <div className="self-stretch flex flex-col justify-start items-start gap-[30px] md:gap-10">
        <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
          <div
            data-プロパティ1="base"
            className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
          >
            <div className="w-1 self-stretch bg-primary rounded-sm" />
            <div className="flex-1 flex justify-start items-center gap-2.5">
              <div className="flex justify-start items-center gap-[5px]">
                <div className="justify-start text-primary text-[18px] md:text-xl font-bold leading-9">
                  新規お問い合わせ
                </div>
              </div>
            </div>

            {/*section*/}
          </div>

          {/* Contact Form */}
          <div className="w-full max-w-4xl">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              className="bg-white flex flex-col gap-3.5"
            >
              {/* お問い合わせ種別 */}
              <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                <div className="w-44 flex justify-start items-center gap-2.5">
                  <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                    お問い合わせ種別
                  </div>
                </div>
                <div className="w-full md:w-auto flex-1">
                  <Form.Item
                    name="type"
                    className="!mb-0"
                    rules={[
                      {
                        required: true,
                        message: "お問い合わせ種別を選択してください",
                      },
                    ]}
                  >
                    <Select
                      placeholder="お問い合わせ種別を選択する"
                      size="large"
                      status={errors.inquiry_type ? "error" : undefined}
                      className="w-full"
                      options={inquiryTypeOptions}
                    ></Select>
                  </Form.Item>
                </div>
              </div>

              {/* 件名 */}
              <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                <div className="w-44 flex justify-start items-center gap-2.5">
                  <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                    件名
                  </div>
                </div>
                <div className="w-full md:w-auto flex-1">
                  <Form.Item
                    name="subject"
                    className="!mb-0"
                    rules={[
                      { required: true, message: "件名を入力してください" },
                    ]}
                  >
                    <Input
                      placeholder="例：出品ID「#00123」の商品について"
                      size="large"
                      status={errors.subject ? "error" : undefined}
                      className="!h-12"
                    />
                  </Form.Item>
                </div>
              </div>

              {/* 内容 */}
              <div className="self-stretch md:inline-flex justify-start items-start gap-5">
                <div className="w-44 py-3 flex justify-start items-start gap-2.5">
                  <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                    内容
                  </div>
                </div>
                <div className="w-full md:w-auto flex-1">
                  <Form.Item
                    name="content"
                    className="!mb-0"
                    rules={[
                      { required: true, message: "内容を入力してください" },
                    ]}
                  >
                    <Input.TextArea
                      placeholder="例：購入申請を承認した後にチャットの返事が返ってこなくなってしまいました。キャンセル申請をしたのですが、許可していただけないので、対応していただきたいです。"
                      rows={6}
                      maxLength={500}
                      showCount
                      status={errors.content ? "error" : undefined}
                      className="text-base"
                    />
                  </Form.Item>
                </div>
              </div>

              {/* 送信ボタン */}
              <div className="self-stretch flex justify-start items-start mt-6">
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  className="w-80 min-h-14 px-5 py-2.5 bg-primary rounded-md !text-white !font-bold !text-[16px] md:!text-lg hover:!bg-primary/90"
                >
                  送信する
                </Button>
              </div>
            </Form>
          </div>

          <div
            data-プロパティ1="base"
            className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
          >
            <div className="w-1 self-stretch bg-primary rounded-sm" />
            <div className="flex-1 flex justify-start items-center gap-2.5">
              <div className="flex justify-start items-center gap-[5px]">
                <div className="justify-start text-primary text-[18px] md:text-xl font-bold leading-9">
                  進行中のお問い合わせ
                </div>
              </div>
            </div>
          </div>
          <Table<Inquiry>
            className="w-full hidden md:block"
            rowKey="code"
            onChange={onChangePageProcess}
            pagination={false}
            columns={columns}
            dataSource={inquiryDataProcess?.data?.items || []}
          />
          <PaginateListMobile<Inquiry>
            pagination={false}
            dataSource={inquiryMobileDataProcess?.data?.items || []}
            renderItem={(item, checked, onSelect) => (
              <InquiryItem<Inquiry>
                inquiry={item}
                checked={checked}
                onSelect={onSelect}
                hideSelect={true}
              />
            )}
          />

          <div
            data-プロパティ1="base"
            className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
          >
            <div className="w-1 self-stretch bg-primary rounded-sm" />
            <div className="flex-1 flex justify-start items-center gap-2.5">
              <div className="flex justify-start items-center gap-[5px]">
                <div className="justify-start text-primary text-[18px] md:text-xl font-bold leading-9">
                  過去のお問い合わせ
                </div>
              </div>
            </div>
            <div
              data-プロパティ1="base"
              className="hidden md:inline-flex justify-start items-center"
            >
              <div className="justify-start text-primary text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug">
                <Link to="/seller/conversation-inquiry/past-list">
                  全ての過去のお問い合わせを見る
                </Link>
              </div>
              <IconKeyboardArrowRightBlue className="w-[20px] h-[20px] aspect-[1/1]" />
            </div>
          </div>
          <Table<Inquiry>
            className="w-full hidden md:block"
            rowKey="code"
            onChange={onChangePageClose}
            pagination={false}
            columns={columns}
            dataSource={inquiryDataClose?.data?.items || []}
          />
          <PaginateListMobile<Inquiry>
            pagination={false}
            dataSource={inquiryMobileDataClose?.data?.items || []}
            renderItem={(item, checked, onSelect) => (
              <InquiryItem<Inquiry>
                inquiry={item}
                checked={checked}
                onSelect={onSelect}
                hideSelect={true}
              />
            )}
          />
          <div
            data-プロパティ1="base"
            className="md:hidden inline-flex justify-start items-center"
          >
            <div className="justify-start text-primary text-sm font-medium font-['Noto_Sans_JP'] underline leading-snug">
              <Link to="/seller/conversation-inquiry/past-list">
                全ての過去のお問い合わせを見る
              </Link>
            </div>
            <IconKeyboardArrowRightBlue className="w-[20px] h-[20px] aspect-[1/1]" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationInquiryPage;
